import type { GanttFeature } from '@/components/kibo-ui/gantt'
import {
  GanttFeatureList,
  GanttFeatureRow,
  GanttHeader,
  GanttProvider,
  GanttSidebar,
  GanttSidebarItem,
  GanttTimeline,
} from '@/components/kibo-ui/gantt'
import { Time } from '@/lib/time/time'
import { DriverActivityType } from '@/modules/activities/consts'
import { TOGGLE_ACTIVITY_EXCLUDE_FROM_SALARY_CALCULATION_MUTATION } from '@/modules/activities/mutations'
import type { ExcludeActivityPromptData } from '@/modules/salary/ExcludeActivityPrompt'
import { ExcludeActivityPrompt } from '@/modules/salary/ExcludeActivityPrompt'
import { PERMISSIONS } from '@/utils/permissions'
import { GRAPH_WORK_PERIOD } from '@/modules/salary/queries'
import type { WorkPeriodNode } from '@/modules/salary/types.graphql'
import {
  durationAsSecondsToString,
  format,
  useActivityColor,
  usePermissions,
} from '@/utils'
import { useMutation, useQuery } from '@apollo/client'
import { usePrompt } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import {
  addHours,
  differenceInMinutes,
  formatISO,
  parseISO,
  subHours,
} from 'date-fns'
import groupBy from 'lodash/groupBy'
import { useState } from 'react'
import { isMobile } from 'react-device-detect'
import { useTheme } from 'styled-components'

const SalaryGantt: React.FC<{
  workPeriodId: string
  outerWorkPeriod?: WorkPeriodNode
  activitiesOnly?: boolean
  outerRefetch?: () => void
}> = ({ workPeriodId, outerWorkPeriod, activitiesOnly, outerRefetch }) => {
  const translations = useTranslate({
    duration: 'common.duration',
    minutes: 'common.minutes',
    nMinutes: ['common.n-minutes', { n: 0 }],
    activitiesForSalary: 'salary.activities-for-salary',
    activities: 'common.activities',
    timesheets: 'common.timesheets',

    work: 'common.work',
    available: 'common.availability',
    pause: 'common.pause',
    paidPause: 'common.paid-pause',
    driving: 'common.driving',
    rest: 'common.rest',
  })
  const { data, refetch } = useQuery(GRAPH_WORK_PERIOD, {
    variables: { id: workPeriodId },
  })
  const theme = useTheme()
  const getActivityColor = useActivityColor()
  const [zoom, setZoom] = useState(100)
  const addPrompt = usePrompt()
  const [toggleActivityPaid] = useMutation(
    TOGGLE_ACTIVITY_EXCLUDE_FROM_SALARY_CALCULATION_MUTATION,
    {
      update(cache, { data }) {
        const activity =
          data?.toggleActivityExcludeFromSalaryCalculation.activity
        if (!activity) return

        cache.modify({
          id: cache.identify(activity),
          fields: {
            excludeFromSalaryCalculation: () =>
              activity.excludeFromSalaryCalculation,
            excludeBefore: () => activity.excludeBefore,
            excludeAfter: () => activity.excludeAfter,
          },
        })
      },
      onCompleted: () => {
        void refetch()
      },
    }
  )

  const { hasPermissions } = usePermissions()

  const canMarkActivitiesAsNotPaid = hasPermissions(
    PERMISSIONS.activities.change.driveractivitypaidstatus
  )

  if (!data?.workPeriod) return <div>Loading...</div>

  const workPeriod = data?.workPeriod ?? null
  if (!workPeriod) return <div>Work period not found</div>
  const datetimeStart = parseISO(workPeriod?.datetimeStart ?? new Date())
  const datetimeEnd = parseISO(workPeriod?.datetimeEnd ?? new Date())
  const ActivityKey = 'Kjøredata'
  const TimesheetsKey = 'Timeliste'
  const GroupKey = 'Lønnsdata'

  const settingsMap = {
    [DriverActivityType.WORK]: translations.work,
    [DriverActivityType.AVAILABLE]: translations.available,
    [DriverActivityType.PAID_REST]: translations.paidPause,
    [DriverActivityType.DRIVE]: translations.driving,
    [DriverActivityType.REST]: translations.pause,
  }

  const activityFeatures: Record<string, GanttFeature[]> = {
    [ActivityKey]: workPeriod.activities.map(activity => ({
      id: activity.id,
      name: '',
      startAt: parseISO(activity.datetimeStart),
      endAt: parseISO(activity.datetimeEnd),
      status: {
        id: activity.activityType,
        name: '',
        color: getActivityColor(activity),
        active: !activity.excludeFromSalaryCalculation,
      },
      duration: activity.duration / 60,
      group: {
        id: ActivityKey,
        name: ActivityKey,
        color: theme.colors.gray11,
      },
      tooltipContent: (
        <div>
          <h3>
            {settingsMap[activity.activityType as keyof typeof settingsMap]}{' '}
            {translations.nMinutes({ n: activity.duration / 60 })}
          </h3>
          <ul>
            <li>
              {format(parseISO(activity.datetimeStart), 'HH:mm')} -{' '}
              {format(parseISO(activity.datetimeEnd), 'HH:mm')}
            </li>
          </ul>
        </div>
      ),
    })),
  }

  const salaryActivityFeatures: Record<string, GanttFeature[]> = {
    [translations.activitiesForSalary]: workPeriod.salaryActivities.map(
      activity => ({
        id: activity.id,
        name: '',
        startAt: parseISO(activity.datetimeStart),
        endAt: parseISO(activity.datetimeEnd),
        group: {
          id: translations.activitiesForSalary,
          name: translations.activitiesForSalary,
          color: getActivityColor(activity),
        },
        tooltipContent: (
          <div>
            <h3>
              {settingsMap[activity.activityType as keyof typeof settingsMap]}{' '}
              {translations.nMinutes({ n: activity.duration / 60 })}
            </h3>
            <ul>
              <li>
                {format(parseISO(activity.datetimeStart), 'HH:mm')} -{' '}
                {format(parseISO(activity.datetimeEnd), 'HH:mm')}
              </li>
            </ul>
          </div>
        ),
      })
    ),
  }

  const timeEntriesFeatures: Record<string, GanttFeature[]> = {
    [TimesheetsKey]: workPeriod.timeEntries
      .filter(entry => !!entry.datetimeEnd)
      .map(entry => ({
        id: entry.id,
        name: '',
        startAt: parseISO(entry.datetimeStart),
        endAt: entry.datetimeEnd
          ? parseISO(entry.datetimeEnd)
          : parseISO(entry.datetimeStart),
        group: {
          id: TimesheetsKey,
          name: TimesheetsKey,
          color: theme.colors.orange,
        },
        tooltipContent: (
          <div>
            <h3>
              {settingsMap[DriverActivityType.WORK as keyof typeof settingsMap]}{' '}
              {new Time(
                ...entry.totalWorkTime.split(':').map(s => parseFloat(s))
              ).reduce('seconds') / 60}{' '}
              {translations.minutes}
            </h3>
            <ul>
              <li>
                {format(parseISO(entry.datetimeStart), 'HH:mm')} -{' '}
                {format(
                  entry.datetimeEnd
                    ? parseISO(entry.datetimeEnd)
                    : parseISO(entry.datetimeStart),
                  'HH:mm'
                )}
              </li>
              <li>{entry.comment}</li>
            </ul>
          </div>
        ),
      })),
  }

  const workPeriodGroupedActivities = activitiesOnly
    ? []
    : (outerWorkPeriod ?? workPeriod).groupedActivities.map((group, i) => ({
        id: (group.salaryId ?? '') + (group.startTime ?? '') + i.toString(),
        name: group.salaryName,
        startAt: parseISO(group.startTime),
        endAt: parseISO(group.endTime),
        owner: null,
        group: {
          id: group.salaryName ?? '',
          name: group.salaryName ?? '',
          color: theme.colors.gray11,
        },
        tooltipContent: (
          <div>
            <h3>
              {group.salaryName ?? ''}{' '}
              {translations.nMinutes({
                n: differenceInMinutes(
                  parseISO(group.endTime),
                  parseISO(group.startTime)
                ),
              })}
            </h3>
            <ul>
              <li>
                {format(parseISO(group.startTime), 'HH:mm')} -{' '}
                {format(parseISO(group.endTime), 'HH:mm')}
              </li>
            </ul>
          </div>
        ),
      }))
  const groupedActivityFeatures: Record<string, GanttFeature[]> = groupBy(
    workPeriodGroupedActivities,
    feature => feature.group.id
  )
  const featuresGroups: Record<string, Record<string, GanttFeature[]>> = {
    [translations.activities]: activityFeatures,
    ...(workPeriod.timeEntries.length > 0
      ? { [translations.timesheets]: timeEntriesFeatures }
      : {}),
    [translations.activitiesForSalary]: salaryActivityFeatures,
    ...(activitiesOnly ? {} : { [GroupKey]: groupedActivityFeatures }),
  }
  const handleViewFeature = async (feature: GanttFeature) => {
    if (workPeriod.approvedByManager || !canMarkActivitiesAsNotPaid) return
    if (!feature.id) return
    const activity = workPeriod.activities.find(a => a.id === feature.id)
    if (!activity) return

    const { data } = await addPrompt<ExcludeActivityPromptData | null>(
      resolve => (
        <ExcludeActivityPrompt activity={activity} onSubmit={resolve} />
      )
    )

    if (!data) return
    await toggleActivityPaid({
      variables: {
        activity: activity.id,
        value: data.excludeFromSalaryCalculation,
        excludeBefore: data.excludeBefore
          ? formatISO(data.excludeBefore)
          : null,
        excludeAfter: data.excludeAfter ? formatISO(data.excludeAfter) : null,
      },
    })
    await outerRefetch?.()
    await refetch()
  }
  const startDatetime = subHours(datetimeStart, 1)
  const endDatetime = addHours(datetimeEnd, 1)
  startDatetime.setMinutes(0, 0, 0)
  endDatetime.setMinutes(0, 0, 0)

  // Create object of featureKey and array of features
  const sidebarItems = Object.values(featuresGroups).flatMap(features =>
    Object.entries(features).map(([featureKey, features]) => ({
      featureKey,
      features,
    }))
  )
  return (
    <div
      className="pb-4 relative"
      style={{ width: isMobile ? '100%' : `${1600}px`, maxWidth: '100%' }}
    >
      {!isMobile && (
        <>
          <div
            className="absolute bg-grey"
            style={{ top: 10, right: 10, zIndex: 2000 }}
          >
            <button
              className="px-2 py-1 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50 cursor-pointer"
              onClick={() => setZoom(z => Math.max(20, z - 20))}
              disabled={zoom <= 20}
            >
              -
            </button>
            <span className="mx-2">{zoom}%</span>
            <button
              className="px-2 py-1 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50 cursor-pointer"
              onClick={() => setZoom(z => Math.min(500, z + 20))}
              disabled={zoom >= 500}
            >
              +
            </button>
          </div>
          <div
            className="absolute bg-grey"
            style={{ bottom: 10, right: 10, zIndex: 2000 }}
          >
            <b>
              {durationAsSecondsToString(
                workPeriod.activities
                  .filter(
                    activity =>
                      !activity.excludeFromSalaryCalculation &&
                      !(activity.activityType === 'Rest')
                  )
                  .reduce((sum, activity) => sum + activity.duration, 0)
              )}
            </b>
          </div>
        </>
      )}
      <GanttProvider
        className="border"
        range="daily"
        zoom={zoom}
        datetimeStart={startDatetime}
        datetimeEnd={endDatetime}
      >
        <GanttSidebar compact={isMobile}>
          {sidebarItems.map(({ featureKey, features }, i) => (
            <GanttSidebarItem
              feature={{
                id: featureKey + i.toString(),
                name: featureKey,
                startAt: features[0]?.startAt ?? new Date(),
                endAt: features[features.length - 1]?.endAt ?? new Date(),
                duration: features.reduce(
                  (sum, feature) =>
                    sum +
                    (feature.duration === undefined
                      ? differenceInMinutes(feature.endAt, feature.startAt)
                      : feature.duration),
                  0
                ),
                status: undefined,
              }}
              key={featureKey + i.toString()}
            />
          ))}
        </GanttSidebar>
        <GanttTimeline>
          <GanttHeader />
          <GanttFeatureList>
            {Object.entries(featuresGroups).map(([group, features]) => {
              const featureArray = Object.values(features)
              if (featureArray.some(f => f.length === 0)) return <></>
              return featureArray.map((featureList, i) => (
                <GanttFeatureRow
                  key={`${workPeriod.id}-${group}-${i}`}
                  features={featureList}
                  handleViewFeature={feature => handleViewFeature(feature)}
                />
              ))
            })}
          </GanttFeatureList>
        </GanttTimeline>
      </GanttProvider>
    </div>
  )
}
export default SalaryGantt
